import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Shield, ArrowLeft, CheckCircle2, Loader2, Sparkles, Globe } from "lucide-react";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { useI18n } from "../i18n/I18nProvider";
import Logo from "../components/Logo";
import { API } from "../App";

const COPY = {
  es: {
    badge: "AGENDAR DEMO",
    title: "Solicita una demostración personalizada",
    subtitle: "Cuéntanos sobre tu organización y un especialista de MSSPRD te contactará en menos de 24 horas.",
    back: "Volver al inicio",
    fields: {
      name: "Nombre completo *",
      email: "Correo corporativo *",
      company: "Empresa / Organización *",
      phone: "Teléfono (opcional)",
      company_size: "Tamaño de la empresa",
      sector: "Sector",
      message: "¿Qué te gustaría ver en la demo? (opcional)",
    },
    sizes: ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"],
    sectors: ["Gobierno", "Banca y finanzas", "Telecomunicaciones", "Zona Franca", "Salud", "Educación", "Energía", "Manufactura", "Tecnología", "Consultoría", "Otro"],
    submit: "Solicitar demostración",
    submitting: "Enviando...",
    success_title: "¡Solicitud recibida!",
    success_body: "Un especialista de MSSPRD te contactará en menos de 24 horas al correo que proporcionaste.",
    success_cta: "Volver al inicio",
    error: "No pudimos enviar tu solicitud. Intenta de nuevo en un momento.",
    rate_limited: "Has enviado demasiadas solicitudes. Por favor espera una hora antes de intentar de nuevo.",
    captcha_required: "Por favor completa la verificación de seguridad antes de enviar.",
    captcha_invalid: "La verificación de seguridad falló. Recarga la página e intenta de nuevo.",
    privacy: "Al enviar este formulario aceptas que MSSPRD use tus datos para contactarte. No compartimos tu información con terceros.",
  },
  en: {
    badge: "BOOK A DEMO",
    title: "Request a personalized demo",
    subtitle: "Tell us about your organization and a MSSPRD specialist will contact you in under 24 hours.",
    back: "Back to home",
    fields: {
      name: "Full name *",
      email: "Work email *",
      company: "Company / Organization *",
      phone: "Phone (optional)",
      company_size: "Company size",
      sector: "Sector",
      message: "What would you like to see in the demo? (optional)",
    },
    sizes: ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"],
    sectors: ["Government", "Banking & Finance", "Telecom", "Free Zone", "Healthcare", "Education", "Energy", "Manufacturing", "Technology", "Consulting", "Other"],
    submit: "Request demo",
    submitting: "Sending...",
    success_title: "Request received!",
    success_body: "A MSSPRD specialist will reach out within 24 hours to the email you provided.",
    success_cta: "Back to home",
    error: "We couldn't send your request. Please try again in a moment.",
    rate_limited: "You've sent too many requests. Please wait one hour before trying again.",
    captcha_required: "Please complete the security check before submitting.",
    captcha_invalid: "Security verification failed. Reload the page and try again.",
    privacy: "By submitting this form you agree that MSSPRD may use your data to contact you. We don't share your information with third parties.",
  },
};

const DemoRequest = () => {
  const { locale, setLocale } = useI18n();
  const lang = locale === "en" ? "en" : "es";
  const t = COPY[lang];
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    company_size: "", sector: "", message: "",
    company_website: "", // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");

  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Load Cloudflare Turnstile and render the widget once on mount.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !window.turnstile || !turnstileRef.current || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "dark",
        callback: (token) => setTurnstileToken(token || ""),
        "error-callback": () => setTurnstileToken(""),
        "expired-callback": () => setTurnstileToken(""),
        "timeout-callback": () => setTurnstileToken(""),
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else if (!document.querySelector(`script[src^="${TURNSTILE_SCRIPT_SRC}"]`)) {
      const script = document.createElement("script");
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.head.appendChild(script);
    } else {
      const poll = setInterval(() => {
        if (window.turnstile) { clearInterval(poll); renderWidget(); }
      }, 100);
      setTimeout(() => clearInterval(poll), 10000);
    }

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) {}
        widgetIdRef.current = null;
      }
    };
  }, []);

  const resetTurnstile = () => {
    setTurnstileToken("");
    if (widgetIdRef.current && window.turnstile) {
      try { window.turnstile.reset(widgetIdRef.current); } catch (_) {}
    }
  };

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const updateSelect = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) return;
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError(t.captcha_required);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact/demo-request`, {
        ...form,
        locale: lang,
        turnstile_token: turnstileToken,
      });
      setSent(true);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        setError(t.rate_limited);
      } else if (status === 400 && detail === "captcha-invalid") {
        setError(t.captcha_invalid);
        resetTurnstile();
      } else {
        setError(t.error);
        resetTurnstile();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans" data-testid="demo-request-page">
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            {/* S9: V2 symbol on dark header */}
            <Logo variant="symbol" className="w-8 h-8" alt="ComplianceOS" />
            <div className="flex flex-col leading-none">
              <span className="font-bold tracking-tight">ComplianceOS</span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">by MSSPRD</span>
            </div>
          </Link>
          <button
            onClick={() => setLocale(lang === "es" ? "en" : "es")}
            className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition border border-white/10 px-2.5 py-1.5 rounded"
            data-testid="lang-toggle"
          >
            <Globe className="w-3 h-3" /> {lang === "es" ? "EN" : "ES"}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-16 md:py-24">
        <Link to="/" className="text-sm text-zinc-500 hover:text-white transition inline-flex items-center gap-1.5 mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> {t.back}
        </Link>

        {sent ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="border border-primary/30 bg-primary/5 p-10 text-center"
            data-testid="demo-success"
          >
            <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-5" />
            <h1 className="text-2xl font-bold font-heading mb-3">{t.success_title}</h1>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">{t.success_body}</p>
            <Button onClick={() => navigate("/")} className="bg-action text-action-foreground hover:bg-action/90 font-semibold">
              {t.success_cta}
            </Button>
          </motion.div>
        ) : (
          <>
            <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 font-medium text-[10px] tracking-[0.2em] px-3 py-1 mb-5">
              <Sparkles className="w-3 h-3 mr-1.5" />{t.badge}
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight font-heading mb-3">{t.title}</h1>
            <p className="text-zinc-400 mb-10">{t.subtitle}</p>

            <form onSubmit={submit} className="space-y-5" data-testid="demo-form">
              {/* Honeypot — hidden from real users */}
              <div className="hidden" aria-hidden="true">
                <label>
                  Company website
                  <input
                    type="text"
                    name="company_website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.company_website}
                    onChange={update("company_website")}
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-zinc-300 text-xs uppercase tracking-wider">{t.fields.name}</Label>
                  <Input id="name" required minLength={2} maxLength={120} value={form.name} onChange={update("name")}
                         className="mt-1.5 bg-[#0C0C0C] border-white/10 text-white" data-testid="field-name" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-zinc-300 text-xs uppercase tracking-wider">{t.fields.email}</Label>
                  <Input id="email" type="email" required value={form.email} onChange={update("email")}
                         className="mt-1.5 bg-[#0C0C0C] border-white/10 text-white" data-testid="field-email" />
                </div>
                <div>
                  <Label htmlFor="company" className="text-zinc-300 text-xs uppercase tracking-wider">{t.fields.company}</Label>
                  <Input id="company" required minLength={2} maxLength={160} value={form.company} onChange={update("company")}
                         className="mt-1.5 bg-[#0C0C0C] border-white/10 text-white" data-testid="field-company" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-zinc-300 text-xs uppercase tracking-wider">{t.fields.phone}</Label>
                  <Input id="phone" type="tel" maxLength={40} value={form.phone} onChange={update("phone")}
                         className="mt-1.5 bg-[#0C0C0C] border-white/10 text-white" data-testid="field-phone" />
                </div>
                <div>
                  <Label htmlFor="company_size" className="text-zinc-300 text-xs uppercase tracking-wider">{t.fields.company_size}</Label>
                  <Select value={form.company_size} onValueChange={updateSelect("company_size")}>
                    <SelectTrigger id="company_size" className="mt-1.5 bg-[#0C0C0C] border-white/10 text-white" data-testid="field-size">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {t.sizes.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="sector" className="text-zinc-300 text-xs uppercase tracking-wider">{t.fields.sector}</Label>
                  <Select value={form.sector} onValueChange={updateSelect("sector")}>
                    <SelectTrigger id="sector" className="mt-1.5 bg-[#0C0C0C] border-white/10 text-white" data-testid="field-sector">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {t.sectors.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-zinc-300 text-xs uppercase tracking-wider">{t.fields.message}</Label>
                <Textarea id="message" rows={4} maxLength={2000} value={form.message} onChange={update("message")}
                          className="mt-1.5 bg-[#0C0C0C] border-white/10 text-white" data-testid="field-message" />
              </div>

              {TURNSTILE_SITE_KEY && (
                <div ref={turnstileRef} className="pt-2" data-testid="turnstile-widget" />
              )}

              {error && (
                <p className="text-sm text-red-400" data-testid="demo-error" role="alert">{error}</p>
              )}

              <Button type="submit" size="lg"
                      disabled={submitting || (TURNSTILE_SITE_KEY && !turnstileToken)}
                      className="bg-action text-action-foreground hover:bg-action/90 font-semibold w-full md:w-auto px-8"
                      data-testid="demo-submit">
                {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.submitting}</>) : t.submit}
              </Button>

              <p className="text-xs text-zinc-500 max-w-xl pt-4 border-t border-white/5">{t.privacy}</p>
            </form>
          </>
        )}
      </main>
    </div>
  );
};

export default DemoRequest;
